import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Delaunay } from "d3-delaunay";

const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
const clamp01 = (v) => Math.min(Math.max(v, 0), 1);
const smooth = (t) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

function mulberry32(seed) {
  return function rand() {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function Environment() {
  const { gl, scene } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = env;
    scene.fog = new THREE.Fog(0x050505, 10, 19);
    return () => {
      scene.environment = null;
      scene.fog = null;
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
}

function CameraRig({ reduced, progressRef }) {
  useFrame(({ camera, pointer }) => {
    const p = progressRef.current;
    // final dolly — we pass through the resolved glass into what follows
    const outF = smooth((p - 0.95) / 0.05);
    const targetZ = 8.5 - outF * 2.6;
    camera.position.z += (targetZ - camera.position.z) * 0.12;
    if (!reduced) {
      camera.position.x += (pointer.x * 0.7 - camera.position.x) * 0.04;
      camera.position.y += (pointer.y * 0.45 - camera.position.y) * 0.04;
    }
    camera.lookAt(0, 0, 0);
  });
  return null;
}

function Shards({ progressRef, count, reduced }) {
  const groupRef = useRef(null);
  const startedAt = useRef(null);
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tmpQ = useMemo(() => new THREE.Quaternion(), []);

  const { faceMats, edgeMats, shards } = useMemo(() => {
    const rand = mulberry32(11);

    // shared glass recipe — one material instance PER FRAGMENT so the fire
    // can clear each panel individually as it passes
    const makeFace = () =>
      new THREE.MeshPhysicalMaterial({
        color: 0x101010,
        metalness: 0.15,
        roughness: 0.08,
        transparent: true,
        opacity: 0.55,
        envMapIntensity: 2.4,
        side: THREE.DoubleSide,
        clearcoat: 1,
        clearcoatRoughness: 0.08,
        depthWrite: false,
      });

    // ONE continuous sheet of glass, cut into interlocking fragments.
    // Seeded jittered-grid points → Voronoi cells → every fragment's edges
    // are exactly its neighbors' edges. The pieces are unique, persistent,
    // and physically rejoin — the same glass breaking and returning.
    const cols = Math.ceil(Math.sqrt(count * 1.9));
    const rows = Math.ceil(count / cols);
    const W = 11;
    const H = 6;
    const pts = [];
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        pts.push([
          ((c + 0.5) / cols - 0.5) * W + (rand() - 0.5) * (W / cols) * 0.7,
          ((r + 0.5) / rows - 0.5) * H + (rand() - 0.5) * (H / rows) * 0.7,
        ]);
      }
    }
    const voronoi = Delaunay.from(pts).voronoi([-W / 2, -H / 2, W / 2, H / 2]);

    const maxD = Math.hypot(W / 2, H / 2);
    const shards = pts.map((pt, i) => {
      const cell = voronoi.cellPolygon(i) || [
        [pt[0] - 0.5, pt[1] - 0.5],
        [pt[0] + 0.5, pt[1] - 0.5],
        [pt[0], pt[1] + 0.5],
        [pt[0] - 0.5, pt[1] - 0.5],
      ];
      let cx = 0;
      let cy = 0;
      for (let j = 0; j < cell.length - 1; j += 1) {
        cx += cell[j][0];
        cy += cell[j][1];
      }
      cx /= cell.length - 1;
      cy /= cell.length - 1;
      const shape = new THREE.Shape();
      cell.forEach(([x, y], j) => (j === 0 ? shape.moveTo(x - cx, y - cy) : shape.lineTo(x - cx, y - cy)));
      const geo = new THREE.ShapeGeometry(shape);
      geo.computeVertexNormals();
      const edgeGeo = new THREE.EdgesGeometry(geo, 1);

      const dir = new THREE.Vector3(cx, cy, 0.001).normalize();
      // pristine orientation: a whisper of facet tilt so the pane catches
      // light unevenly, like real sheet glass
      const qFinal = new THREE.Quaternion().setFromEuler(
        new THREE.Euler((rand() - 0.5) * 0.09, (rand() - 0.5) * 0.09, 0)
      );
      return {
        geo,
        edgeGeo,
        target: new THREE.Vector3(cx, cy, (rand() - 0.5) * 0.05),
        scatter: new THREE.Vector3(
          cx * 2.4 + (rand() - 0.5) * 6,
          cy * 2.4 + (rand() - 0.5) * 6,
          -5 - rand() * 7
        ),
        explode: new THREE.Vector3(
          dir.x * (2.5 + rand() * 4.5),
          dir.y * (2 + rand() * 3.5),
          1.5 + rand() * 5
        ),
        rotAxis: new THREE.Vector3(rand() - 0.5, rand() - 0.5, rand() - 0.5).normalize(),
        rotSpeed: 0.6 + rand() * 2.4,
        baseRot: rand() * Math.PI * 2,
        qFinal,
        delay: rand() * 0.45,
        delayC: rand() * 0.1,
        // the fire travels: ignition time is ordered by distance from the
        // pane's center, so veins sweep outward through the seams
        fireDelay: (Math.hypot(cx, cy) / maxD) * 0.08,
        wobble: rand() * Math.PI * 2,
        fire: rand() < 0.4 ? 0 : rand() < 0.5 ? 1 : 2, // 0 none · 1 crimson · 2 blue
      };
    });
    // one edge material per fragment — the fire follows each piece's own
    // fracture lines and timing, never a blanket outline
    const edgeMats = shards.map(
      () => new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.3 })
    );
    const faceMats = shards.map(makeFace);
    return { faceMats, edgeMats, shards };
  }, [count]);

  useEffect(() => {
    return () => {
      faceMats.forEach((m) => m.dispose());
      edgeMats.forEach((m) => m.dispose());
      shards.forEach((s) => {
        s.geo.dispose();
        s.edgeGeo.dispose();
      });
    };
  }, [faceMats, edgeMats, shards]);

  useFrame(({ clock, viewport }) => {
    if (!groupRef.current) return;
    if (startedAt.current === null) startedAt.current = clock.elapsedTime;
    const t = clock.elapsedTime - startedAt.current;
    const p = progressRef.current;

    // ---- timeline ------------------------------------------------------
    // assemble   : time-based intro (untouched)
    // fracture   : p 0.05 → 0.38   (BREAK APART)
    // drift      : p 0.38 → 0.48   (RETHINK — pieces move independently)
    // converge   : p 0.48 → 0.80   (REBUILD — pieces return to their seats)
    // fire       : p 0.50 → 0.78   (veins sweep the seams, center → edge)
    // clarity    : p 0.72 → 0.94   (panels turn transparent behind the fire)
    // hold       : p 0.86 → 0.95   (the clear glass registers)
    // transition : p 0.95 → 1.00   (we pass through the pane)
    const assemble = reduced ? 1 : easeOutExpo(Math.min(t / 2.4, 1));
    const fracture = smooth((p - 0.05) / 0.33);
    const convMid = smooth((p - 0.48) / 0.28);
    const outF = smooth((p - 0.95) / 0.05);

    // ---- frame the formation so it never crops on narrow viewports -----
    const fit = Math.min(1, viewport.width / 12.4);
    groupRef.current.scale.setScalar(fit * (1 + outF * 1.7));
    groupRef.current.rotation.y = (1 - convMid) * 0.12 + convMid * 0.05;
    groupRef.current.rotation.x = (1 - convMid) * -0.05 + convMid * -0.03;

    const kids = groupRef.current.children;
    for (let i = 0; i < kids.length; i += 1) {
      const m = kids[i];
      const s = shards[i];
      // break apart
      const ef = smooth(fracture * 1.45 - s.delay);
      // rethink → rebuild: each fragment returns to its ORIGINAL seat —
      // same geometry, same position, orientation perfected
      const c = smooth((p - 0.48 - s.delayC) / 0.22);

      tmp.copy(s.scatter).lerp(s.target, assemble);
      tmp.addScaledVector(s.explode, ef);
      tmp.lerp(s.target, c);
      if (!reduced) {
        const free = 1 - c; // wobble dies as the piece locks into place
        tmp.y += Math.sin(t * 0.6 + s.wobble) * 0.06 * free;
        tmp.x += Math.cos(t * 0.4 + s.wobble) * 0.04 * free;
      }
      m.position.copy(tmp);

      tmpQ.setFromAxisAngle(
        s.rotAxis,
        (1 - assemble) * s.baseRot + ef * s.rotSpeed * 2 * (1 - c) + (reduced ? 0 : t * 0.04 * (1 - c))
      );
      m.quaternion.copy(s.qFinal).multiply(tmpQ);

      // fire sweeps the pane center → edge (fireDelay): a thin vein ignites
      // along this piece's seams, peaks, and burns out — and behind it the
      // panel turns genuinely transparent and its seams vanish
      const fireT = clamp01(p - 0.5 - s.fireDelay);
      const fire = smooth(fireT / 0.05) * (1 - smooth((fireT - 0.12) / 0.1));
      const clear1 = smooth((p - 0.72 - s.fireDelay) / 0.14);

      const fm = faceMats[i];
      fm.opacity = (0.55 - clear1 * 0.43) * (1 - outF); // → 0.12: clear sheen, never gray
      fm.envMapIntensity = 2.4 + clear1 * 1.6;

      const em = edgeMats[i];
      if (s.fire === 1) {
        em.color.setRGB(1 - fire * 0.28, 1 - fire * 0.93, 1 - fire * 0.87); // deep crimson
      } else if (s.fire === 2) {
        em.color.setRGB(1 - fire * 0.78, 1 - fire * 0.62, 1); // controlled electric blue
      }
      em.opacity =
        (0.3 * (1 - clear1) + fire * 0.5 * (s.fire ? 1 : 0.25) * (1 - clear1 * 0.5)) * (1 - outF);
    }
  });

  return (
    <group ref={groupRef}>
      {shards.map((s, i) => (
        <mesh key={i} geometry={s.geo} material={faceMats[i]}>
          <lineSegments geometry={s.edgeGeo} material={edgeMats[i]} />
        </mesh>
      ))}
    </group>
  );
}

export default function GlassScene({ progressRef, reduced }) {
  const [count, setCount] = useState(46);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setCount(mq.matches ? 22 : 46);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <Canvas
      data-testid="hero-glass-canvas"
      dpr={[1, 1.75]}
      camera={{ position: [0, 0, 8.5], fov: 42 }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      style={{ position: "absolute", inset: 0, touchAction: "pan-y" }}
    >
      <Environment />
      <CameraRig reduced={reduced} progressRef={progressRef} />
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 7, 6]} intensity={2.4} />
      <directionalLight position={[-6, -4, 3]} intensity={0.8} color="#cfe4ff" />
      <Shards progressRef={progressRef} count={count} reduced={reduced} />
    </Canvas>
  );
}
